/**
 * Configuration Detail Panel Component
 * 
 * Shows detailed configuration information when a matrix cell is selected.
 * Displays grid dimensions, margins, alignment instructions, and Illustrator setup.
 */

import { 
  calculatePageAlignment,
  getAlignmentInstructions
} from '../grid/alignment.js';
import { formatNumber, attachCopyHandlers } from './utils.js';

/**
 * Create configuration detail panel
 * @param {HTMLElement} container - Container element to render into
 * @returns {Object} Panel component with methods for updating content
 */
export function createConfigurationPanel(container) {
  // Clear container
  container.innerHTML = '';
  
  // Create main panel
  const panel = document.createElement('div');
  panel.className = 'configuration-panel';
  
  // Create header
  const header = document.createElement('div');
  header.className = 'panel-header';
  header.textContent = 'Configuration Details';
  panel.appendChild(header);
  
  // Create content area
  const content = document.createElement('div');
  content.className = 'panel-content';
  panel.appendChild(content);
  
  // Initial empty state
  showEmptyState(content);
  
  container.appendChild(panel);
  
  return {
    element: panel,
    showConfiguration: (cellData, formData, matrixCoords) => {
      showConfigurationDetailsV2(content, cellData, formData, matrixCoords);
    },
    showEmpty: () => showEmptyState(content),
    hide: () => {
      panel.style.display = 'none';
    },
    show: () => {
      panel.style.display = 'block';
    }
  };
}

/**
 * Show empty state when no cell is selected
 * @param {HTMLElement} content - Content container
 */
function showEmptyState(content) {
  content.innerHTML = `
    <div class="empty-state">
      <div>📐</div>
      <div class="text-base font-medium mb-2">No Configuration Selected</div>
      <div class="text-sm">Click on a matrix cell to view configuration details</div>
    </div>
  `;
}

// Removed legacy showConfigurationDetails (v1) in favor of V2

/**
 * New hierarchical configuration details with copy-ready values
 */
function showConfigurationDetailsV2(content, cellData, formData, matrixCoords) {
  const { configurations, count } = cellData;
  const { unit, lineHeight, cols, rows, colGutter, rowGutter, pageWidth, pageHeight } = formData;
  const config = configurations[0];

  const colWidthAbs = config.colWidth * lineHeight;
  const rowHeightAbs = config.rowHeight * lineHeight;
  const colGutterAbs = colGutter * lineHeight;
  const rowGutterAbs = rowGutter * lineHeight;
  const contentAreaWidth = pageWidth - (config.leftMargin + (config.rightMargin ?? config.leftMargin));
  const contentAreaHeight = pageHeight - (config.topMargin + (config.bottomMargin ?? config.topMargin));

  const pageAlignment = calculatePageAlignment(config, lineHeight);
  const { adjustedPosition, hasAdjustments } = pageAlignment;

  const PT_TO_PX = 96 / 72;
  const toPx = (v) => unit === 'px' ? v : v * PT_TO_PX;

  const card = (label, value, u = '') => {
    const isNumber = typeof value === 'number' && isFinite(value);
    const display = isNumber ? `${formatNumber(value)}${u}` : `${value}${u}`;
    const copyVal = String(value);
    return `
      <div class="kv-card group bg-gray-50 border border-gray-200 rounded-lg p-3 w-[140px] min-w-[140px] max-w-[140px] cursor-pointer relative" data-copy="${copyVal}">
        <div class="kv-label text-[10px] text-gray-500 mb-0.5 truncate">${label}</div>
        <div class="kv-value flex items-center justify-between gap-2 bg-transparent border-0 p-0 font-mono text-[13px] text-gray-900 whitespace-nowrap">
          <span>${display}</span>
          <svg class="kv-copy w-4 h-4 text-gray-400 group-hover:text-gray-600" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1z"/>
            <path d="M20 5H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h12v14z"/>
          </svg>
        </div>
      </div>`;
  };

  const section = (title, body, icon = '📘') => `
    <div class="detail-section">
      <h3 class="section-title"><span>${icon}</span> ${title}</h3>
      <div class="kv-grid">${body}</div>
    </div>`;

  const basic = [
    card('Rows × Columns', `${rows}×${cols}`, ''),
    card('Row Height', rowHeightAbs, unit),
    card('Column Width', colWidthAbs, unit),
    card('Row Gutter', rowGutterAbs, unit),
    card('Column Gutter', colGutterAbs, unit),
    card('Top Offset', config.topMargin, unit),
    card('Left Offset', config.leftMargin, unit)
  ].join('');

  const illustrator = `
    <div class="kv-fullrow-note">Always set Reference Point to Top Left</div>
    ${card('Document Grid Size', lineHeight, unit)}
    ${card('Artboard X', adjustedPosition.x, unit)}
    ${card('Artboard Y', adjustedPosition.y, unit)}
    ${card('Content Width', contentAreaWidth, unit)}
    ${card('Content Height', contentAreaHeight, unit)}
    ${card('Content Top Offset', config.topMargin, unit)}
    ${card('Content Left Offset', config.leftMargin, unit)}
    ${card('Rows (count)', rows, '')}
    ${card('Row Height', rowHeightAbs, unit)}
    ${card('Row Gutter', rowGutterAbs, unit)}
    ${card('Columns (count)', cols, '')}
    ${card('Column Width', colWidthAbs, unit)}
    ${card('Column Gutter', colGutterAbs, unit)}
    ${hasAdjustments ? `<div class=\"kv-inline-note\">Note: Illustrator artboard position fields accept at most two decimal places. Values are rounded down to this precision and content offsets (margins) are compensated by the rounding error so the page grid stays exactly aligned with the document grid.</div>` : ''}
    <div class="kv-helptext">
      To place additional artboards with page grids perfectly aligned to the document grid, use:
      X = ${formatNumber(adjustedPosition.x)}${unit} + n × ${formatNumber(lineHeight)}${unit},
      Y = ${formatNumber(adjustedPosition.y)}${unit} + n × ${formatNumber(lineHeight)}${unit}
      (for any integer n). Illustrator accepts arithmetic expressions in position fields, e.g. <code>${formatNumber(adjustedPosition.x)} + 3*${formatNumber(lineHeight)}</code>.
    </div>
  `;

  const figma = unit === 'px' ? `
    ${card('Row Grid → Row Height', rowHeightAbs, 'px')}
    ${card('Row Grid → Top Offset', config.topMargin, 'px')}
    ${card('Row Grid → Gutter', rowGutterAbs, 'px')}
    ${card('Column Grid → Column Width', colWidthAbs, 'px')}
    ${card('Column Grid → Left Offset', config.leftMargin, 'px')}
    ${card('Column Grid → Gutter', colGutterAbs, 'px')}
  ` : '';

  content.innerHTML = `
    <div class="configuration-details">
      ${section('Basic Information', basic, '📌')}
      ${section('For Usage in Illustrator', illustrator, '🎨')}
      ${unit === 'px' ? section('For Usage in Figma', figma, '🎛️') : ''}
    </div>
  `;

  attachCopyHandlers(content);
}
